import { useMemo, useState, type ReactNode } from "react";

import {
  useActivateUser,
  useDeleteUser,
  useResendInvite,
  useSuspendUser,
  useUpdateUserRole,
  useUsersList,
} from "../../api/queries";
import type { User, UserRole } from "../../api/types";
import { AbilityGate, granted } from "../../auth/AbilityGate";
import { useAuth } from "../../auth/auth-context";
import { fileDownload } from "../../lib/csv";
import { cx } from "../../lib/cx";
import { useDebounced } from "../../lib/useDebounced";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { inputClassName } from "../../ui/Field";
import { Menu, type MenuItem } from "../../ui/Menu";
import { SettingsPage } from "../../ui/SettingsSection";
import { useToast } from "../../ui/toast-context";
import { InviteDialog } from "./InviteDialog";
import {
  formatLastActive,
  memberTabs,
  membersCsv,
  roleLabels,
  roleOptions,
  userStatus,
  type MemberTab,
} from "./people";

const PAGE_SIZE = 25;

function RoleBadge({ role }: { role: UserRole | undefined }): ReactNode {
  if (role === undefined) return null;
  return (
    <span
      className={cx(
        "rounded-full border px-2 py-0.5 text-xs",
        role === "admin" ? "border-accent text-accent" : "border-line text-muted",
      )}
    >
      {roleLabels[role]}
    </span>
  );
}

function Avatar({ user }: { user: User }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hover text-xs font-semibold"
      style={user.color === undefined ? undefined : { backgroundColor: user.color }}
    >
      {user.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The workspace roster. Tabs are structured filters, search is `query`, and the
 * per-row menu is assembled from that row's own policy — abilities are per user
 * (`docs/spec/permissions.md` §7.5), so the menu for a suspended colleague and
 * the menu for yourself are legitimately different.
 */
export function MembersScreen(): ReactNode {
  const { session } = useAuth();
  const teamId = session?.info.team.id;
  const selfId = session?.info.user.id;

  const [tab, setTab] = useState<MemberTab>("all");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const debouncedSearch = useDebounced(search);

  const filters = useMemo(() => memberTabs.find((entry) => entry.id === tab)?.filters ?? [], [tab]);
  const list = useUsersList({ query: debouncedSearch, filters, limit: PAGE_SIZE, offset });
  const toast = useToast();

  const updateRole = useUpdateUserRole();
  const suspend = useSuspendUser();
  const activate = useActivateUser();
  const resendInvite = useResendInvite();
  const remove = useDeleteUser();

  const users = list.data?.users ?? [];
  const total = list.data?.total ?? 0;
  const abilities = list.data?.abilities ?? {};

  const onError = (error: Error): void => {
    toast.show(error.message, "error");
  };
  const onDone = (message: string): void => {
    toast.show(message);
  };

  const select = (next: MemberTab): void => {
    setTab(next);
    setOffset(0);
  };

  const menuItems = (user: User): MenuItem[] => {
    const can = abilities[user.id] ?? {};
    const status = userStatus(user);
    const items: MenuItem[] = [];

    if (granted(can, "promote") || granted(can, "demote")) {
      for (const option of roleOptions) {
        if (option.value === user.role) continue;
        items.push({
          label: `Make ${option.label.toLowerCase()}`,
          onSelect: () => {
            updateRole.mutate(
              { id: user.id, role: option.value },
              {
                onSuccess: () => {
                  onDone(`${user.name} is now ${option.label.toLowerCase()}.`);
                },
                onError,
              },
            );
          },
        });
      }
    }

    if (status === "Invited" && granted(can, "resendInvite")) {
      items.push({
        label: "Resend invitation",
        onSelect: () => {
          resendInvite.mutate(
            { id: user.id },
            {
              onSuccess: () => {
                onDone(`Invitation resent to ${user.name}.`);
              },
              onError,
            },
          );
        },
      });
    }

    if (status === "Suspended") {
      if (granted(can, "activate")) {
        items.push({
          label: "Activate",
          onSelect: () => {
            activate.mutate(
              { id: user.id },
              {
                onSuccess: () => {
                  onDone(`${user.name} can sign in again.`);
                },
                onError,
              },
            );
          },
        });
      }
    } else if (granted(can, "suspend")) {
      items.push({
        label: "Suspend",
        onSelect: () => {
          suspend.mutate(
            { id: user.id },
            {
              onSuccess: () => {
                onDone(`${user.name} is suspended.`);
              },
              onError,
            },
          );
        },
      });
    }

    if (granted(can, "delete")) {
      items.push({
        label: "Delete account…",
        destructive: true,
        onSelect: () => {
          setPendingDelete(user);
        },
      });
    }

    return items;
  };

  return (
    <SettingsPage
      title="Members"
      description="Everyone in this workspace, what they may do, and how to change it."
      actions={
        <AbilityGate id={teamId} ability="inviteUser">
          <Button
            variant="primary"
            onClick={() => {
              setInviting(true);
            }}
          >
            Invite people
          </Button>
        </AbilityGate>
      }
    >
      <div role="tablist" aria-label="Filter members" className="mb-4 flex flex-wrap gap-1">
        {memberTabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => {
              select(entry.id);
            }}
            className={cx(
              "rounded-md px-3 py-1.5 text-sm",
              tab === entry.id ? "bg-hover font-medium text-ink" : "text-muted hover:bg-hover",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label className="sr-only" htmlFor="member-search">
          Search members
        </label>
        <input
          id="member-search"
          type="search"
          value={search}
          placeholder="Search by name or email"
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
          className={inputClassName}
        />
        <Button
          onClick={() => {
            fileDownload.save("members.csv", membersCsv(users));
          }}
          disabled={users.length === 0}
        >
          Export CSV
        </Button>
      </div>

      {list.isPending ? (
        <p role="status" className="text-sm text-muted">
          Loading members…
        </p>
      ) : list.isError ? (
        <p role="alert" className="text-sm text-danger">
          {list.error.message}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted">No members match this view.</p>
      ) : (
        <ul aria-label="Members" className="divide-y divide-line rounded-md border border-line">
          {users.map((user) => (
            <li key={user.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar user={user} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {user.name}
                  {user.id === selfId && <span className="ml-2 text-xs text-muted">You</span>}
                </p>
                <p className="truncate text-xs text-muted">{user.email ?? "—"}</p>
              </div>
              <RoleBadge role={user.role} />
              <span className="w-28 text-right text-xs text-muted">
                {userStatus(user) === "Active"
                  ? formatLastActive(user.lastActiveAt)
                  : userStatus(user)}
              </span>
              <Menu
                buttonLabel={`Actions for ${user.name}`}
                menuLabel={`Actions for ${user.name}`}
                items={menuItems(user)}
              />
            </li>
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted">
          <span>{`${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={offset === 0}
              onClick={() => {
                setOffset(Math.max(0, offset - PAGE_SIZE));
              }}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => {
                setOffset(offset + PAGE_SIZE);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <InviteDialog
        open={inviting}
        onClose={() => {
          setInviting(false);
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this account?"
        description={`${pendingDelete?.name ?? ""} is removed permanently. Their documents and comments stay, reattributed to an anonymous author. This cannot be undone.`}
        confirmLabel="Delete account"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          const target = pendingDelete;
          if (target === null) return;
          remove.mutate(
            { id: target.id },
            {
              onSuccess: () => {
                setPendingDelete(null);
                onDone(`${target.name} was deleted.`);
              },
              onError: (error) => {
                setPendingDelete(null);
                onError(error);
              },
            },
          );
        }}
      />
    </SettingsPage>
  );
}
